/**
 * cellHtml — escaping helpers for MusubiTabulator column `format` functions.
 *
 * MusubiTabulator renders whatever a column `format` returns as raw HTML
 * (Tabulator treats string returns as innerHTML). Formatters legitimately
 * build badges/links/buttons, so the output cannot be blanket-escaped — but
 * every DYNAMIC value interpolated into that HTML (row data, server strings,
 * scraped prospect fields, public form input) MUST be escaped at the point of
 * interpolation. Route all such values through `esc()`, and all `href`/`src`
 * URLs through `safeUrl()`.
 *
 *   format: (v, row) => `<span class="badge" title="${esc(row.label)}">${esc(v)}</span>`
 *   format: (v, row) => `<a href="${safeUrl(row.url)}" target="_blank" rel="noopener">${esc(v)}</a>`
 *
 * Static, developer-authored strings (class names, fixed labels) don't need
 * escaping — only values that originate outside the code.
 */

const HTML_ENTITIES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/**
 * HTML-escape a value for safe interpolation into element text OR a
 * double/single-quoted attribute. Null/undefined become an empty string.
 * @param {*} v
 * @returns {string}
 */
export function esc(v) {
  if (v === null || v === undefined) return '';
  return String(v).replace(/[&<>"']/g, (ch) => HTML_ENTITIES[ch]);
}

/**
 * Sanitise a URL for use in an href/src attribute. HTML-escaping alone does
 * NOT neutralise `javascript:`/`data:` scheme payloads (they contain no
 * HTML-special characters), so scheme-check first, then escape. Allows
 * http(s), mailto, tel, and relative/anchor URLs; anything else → '#'.
 * @param {*} v
 * @returns {string}
 */
export function safeUrl(v) {
  if (v === null || v === undefined) return '#';
  const raw = String(v).trim();
  if (raw === '') return '#';
  // Relative, root-relative, anchor and query URLs are safe (no scheme).
  if (/^(?:[/?#]|\.\.?\/)/.test(raw)) return esc(raw);
  // Absolute URL: only allow a known-safe scheme.
  if (/^(?:https?:|mailto:|tel:)/i.test(raw)) return esc(raw);
  // Bare domain (no scheme, no leading slash) — treat as https.
  if (/^[a-z0-9.-]+\.[a-z]{2,}(?:[/:?#]|$)/i.test(raw)) return esc(raw);
  return '#';
}
